import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { apiPaths, getInstutueApp } from "../platform-components/api-integration-util";
import Grid from "../platform-components/grid/Grid";
import { getUserDetails } from "../platform-components/utils/shared-data.service";

const CONTENT_TYPES = [
  { value: "FLASHCARD", label: "Flashcards" },
  { value: "VIDEO", label: "Videos" },
  { value: "NOTES", label: "Notes" },
  { value: "QBANK_MODULE", label: "Question Bank Modules" },
  { value: "TEST_MODULE", label: "Test Modules" },
];

const UNCATEGORIZED = "Uncategorized";

const StarRating = ({ value, showNumber = true }) => {
  if (value == null) return <span>-</span>;
  const rounded = Math.round(value * 2) / 2;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((star) => {
        if (star <= Math.floor(rounded)) {
          return <StarIcon key={star} style={{ color: "#f5a623", fontSize: 18 }} />;
        }
        if (star - 0.5 === rounded) {
          return <StarHalfIcon key={star} style={{ color: "#f5a623", fontSize: 18 }} />;
        }
        return <StarBorderIcon key={star} style={{ color: "#ccc", fontSize: 18 }} />;
      })}
      {showNumber && (
        <span style={{ fontSize: 13, color: "#555", marginLeft: 4 }}>
          {Number(value).toFixed(1)}
        </span>
      )}
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const ContentFeedback = () => {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contentType, setContentType] = useState("FLASHCARD");
  const [expandedSubject, setExpandedSubject] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsRows, setDetailsRows] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);

  const getHeaders = () => {
    const user = getUserDetails();
    return { tenantId: user.tenantId, userEmail: user.email };
  };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getInstutueApp(
        apiPaths.getContentFeedback,
        { contentType },
        getHeaders()
      );
      setSummaryData(response.result || []);
    } catch (error) {
      console.error("Error fetching content feedback summary:", error);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [contentType]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const subjectGroups = useMemo(() => {
    const groups = new Map();
    for (const row of summaryData) {
      const key = row.subjectName || UNCATEGORIZED;
      if (!groups.has(key)) {
        groups.set(key, { subject: key, items: [], ratingSum: 0, ratingCount: 0, feedbackCount: 0 });
      }
      const g = groups.get(key);
      g.items.push(row);
      g.ratingSum += (row.avgRating || 0) * (row.ratingCount || 0);
      g.ratingCount += row.ratingCount || 0;
      g.feedbackCount += row.feedbackCount || 0;
    }
    const list = Array.from(groups.values()).map((g) => ({
      ...g,
      avgRating: g.ratingCount > 0 ? g.ratingSum / g.ratingCount : null,
    }));
    list.sort((a, b) => {
      if (a.subject === UNCATEGORIZED) return 1;
      if (b.subject === UNCATEGORIZED) return -1;
      return a.subject.localeCompare(b.subject);
    });
    return list;
  }, [summaryData]);

  useEffect(() => {
    setExpandedSubject(subjectGroups.length > 0 ? subjectGroups[0].subject : null);
  }, [subjectGroups]);

  const openDetails = async (row) => {
    setSelectedContent(row);
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsRows([]);
    try {
      const response = await getInstutueApp(
        apiPaths.getContentFeedbackDetails,
        { contentType, contentId: row.contentId },
        getHeaders()
      );
      setDetailsRows(response.result || []);
    } catch (error) {
      console.error("Error fetching content feedback details:", error);
      setDetailsRows([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedContent(null);
    setDetailsRows([]);
  };

  const columnDefs = [
    {
      headerName: "Content Name",
      field: "contentName",
      tooltipField: "contentName",
      filter: true,
      flex: 2,
      cellRendererFramework: (params) => (
        <span
          style={{ color: "#3366FF", cursor: "pointer", fontWeight: 500 }}
          onClick={() => openDetails(params.data)}
        >
          {params.value}
        </span>
      ),
    },
    {
      headerName: "Avg Rating",
      field: "avgRating",
      filter: "agNumberColumnFilter",
      width: 200,
      sort: "desc",
      cellRendererFramework: (params) => <StarRating value={params.value} />,
    },
    {
      headerName: "# Ratings",
      field: "ratingCount",
      filter: "agNumberColumnFilter",
      width: 120,
    },
    {
      headerName: "# Comments",
      field: "feedbackCount",
      filter: "agNumberColumnFilter",
      width: 130,
    },
    {
      headerName: "Last Feedback",
      field: "lastUpdated",
      filter: true,
      width: 200,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  const totalItems = summaryData.length;

  return (
    <Box sx={{ pt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="content-type-label">Content Type</InputLabel>
          <Select
            labelId="content-type-label"
            value={contentType}
            label="Content Type"
            onChange={(e) => setContentType(e.target.value)}
            MenuProps={{ disablePortal: false }}
          >
            {CONTENT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: "#666" }}>
          {!loading &&
            `${totalItems} ${totalItems === 1 ? "item" : "items"} rated across ${subjectGroups.length} ${subjectGroups.length === 1 ? "subject" : "subjects"}`}
        </Typography>
      </Box>

      {loading && <p>Loading...</p>}
      {!loading && subjectGroups.length === 0 && (
        <p style={{ color: "#999" }}>No feedback found for this content type.</p>
      )}

      {!loading &&
        subjectGroups.map((group) => (
          <Accordion
            key={group.subject}
            expanded={expandedSubject === group.subject}
            onChange={(_, isExpanded) =>
              setExpandedSubject(isExpanded ? group.subject : null)
            }
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  flexWrap: "wrap",
                  gap: 2,
                  pr: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {group.subject}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${group.items.length} ${group.items.length === 1 ? "item" : "items"}`}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                  <StarRating value={group.avgRating} />
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {group.ratingCount} {group.ratingCount === 1 ? "rating" : "ratings"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {group.feedbackCount} {group.feedbackCount === 1 ? "comment" : "comments"}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid
                gridOptions={{
                  columnDefs,
                  rowData: group.items,
                  pagination: group.items.length > 10,
                  paginationPageSize: 10,
                  domLayout: group.items.length <= 10 ? "autoHeight" : undefined,
                }}
                parent={`content-feedback-${group.subject}`}
              />
            </AccordionDetails>
          </Accordion>
        ))}

      <Dialog open={detailsOpen} fullWidth onClose={closeDetails} maxWidth="md">
        <DialogTitle sx={{ borderBottom: "1px solid gainsboro", pr: 6 }}>
          {selectedContent ? selectedContent.contentName : "Feedback Details"}
          {selectedContent && selectedContent.subjectName && (
            <Typography variant="caption" sx={{ display: "block", color: "#888", mt: 0.5 }}>
              {selectedContent.subjectName}
            </Typography>
          )}
          <IconButton
            onClick={closeDetails}
            sx={{ position: "absolute", top: 8, right: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedContent && (
            <Box sx={{ display: "flex", gap: 3, alignItems: "center", mt: 1, mb: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Average
                </Typography>
                <StarRating value={selectedContent.avgRating} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Ratings
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedContent.ratingCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Comments
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedContent.feedbackCount}
                </Typography>
              </Box>
            </Box>
          )}

          {detailsLoading && <p>Loading...</p>}
          {!detailsLoading && detailsRows.length === 0 && (
            <p style={{ color: "#999" }}>No student feedback recorded.</p>
          )}
          {!detailsLoading && detailsRows.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {detailsRows.map((row, idx) => (
                <Box
                  key={idx}
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: "#fafbfc",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ color: "#3366FF", fontWeight: 500 }}>
                      {row.studentEmail}
                    </Typography>
                    <StarRating value={row.rating} showNumber={false} />
                  </Box>
                  {row.feedback && (
                    <Typography sx={{ mt: 1, color: "#333" }}>
                      {row.feedback}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "#888", display: "block", mt: 0.5 }}>
                    {formatDate(row.updatedDate)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContentFeedback;
